import React from 'react';
import { create } from 'jss';
import preset from 'jss-preset-default';
import { MuiThemeProvider, createGenerateClassName, StylesProvider, createTheme } from '@material-ui/core/styles';

import { graff } from '../graff';
import { DEFAULT } from '../theme';

// see :
// https://material-ui.com/guides/migration-v3/
// https://material-ui.com/styles/api/#main-content
// https://material-ui.com/styles/advanced/

//-------------------------------//
//-------------- JSS ------------//
//-------------------------------//
const jss = create(preset());

// very important for react-await-dialog
const generateClassName = createGenerateClassName();

//-------------------------------//
//------- contentLayout ---------//
//-------------------------------//
const { color, radius, shadow, action, font } = graff;

// Material UI's 25 elevations, on the charter's three soft shadows.
const shadows = ['none', ...Array(4).fill(shadow.sm), ...Array(8).fill(shadow.md), ...Array(12).fill(shadow.lg)];

const display = { fontFamily: font.display, fontWeight: 400, letterSpacing: '.3px' };

// The Graff look (direction « Graff soigné », docs/design/bo-maquette-A-graff-soigne.html): light and airy,
// soft shadows, pink with the ink outline and the drip shadow for the main action only, cyan for what is active.
// The legacy keys of theme.js (theme.border.*, theme.background.*, theme.color.*, theme.boxShadow, theme.textShadow)
// are carried too, so makeStyles((theme) => …) finds both kinds of key under this provider.
const theme = createTheme({
	...DEFAULT,
	palette: {
		type: 'light',
		primary: { main: color.pink, dark: color.pinkDeep, light: color.pinkSoft, contrastText: '#fff' },
		secondary: { main: color.cyan, dark: color.cyanInk, light: color.cyanSoft, contrastText: color.ink },
		error: { main: color.danger },
		background: { default: color.wall, paper: color.surface },
		text: { primary: color.ink, secondary: color.muted },
		divider: color.border,
	},
	typography: {
		useNextVariants: true,
		fontFamily: font.body,
		h1: display,
		h2: display,
		h3: display,
		h4: display,
		h5: display,
		h6: display,
		button: { fontWeight: 700, textTransform: 'none' },
	},
	shape: { borderRadius: radius.sm },
	shadows,
	overrides: {
		//---- buttons ----//
		MuiButton: {
			root: {
				borderRadius: 999,
				padding: '8px 18px',
			},
			text: {
				padding: '8px 14px',
			},
			textPrimary: {
				color: color.pinkDeep,
			},
			textSecondary: {
				color: color.cyanInk,
			},
			outlined: {
				padding: '7px 17px',
				borderColor: color.borderStrong,
			},
			outlinedPrimary: {
				color: color.pinkDeep,
				borderColor: 'rgba(224,20,110,.5)',
			},
			outlinedSecondary: {
				color: color.cyanInk,
				borderColor: 'rgba(10,110,134,.5)',
			},
			contained: {
				padding: '8px 18px',
				color: color.ink,
				backgroundColor: color.surface,
				boxShadow: `${shadow.sm}, inset 0 0 0 1.5px ${color.borderStrong}`,
				'&:hover': {
					backgroundColor: color.sunken,
					boxShadow: `${shadow.sm}, inset 0 0 0 1.5px ${color.borderStrong}`,
				},
				'&:active': {
					boxShadow: `inset 0 0 0 1.5px ${color.borderStrong}`,
				},
				'&$focusVisible': {
					boxShadow: `${graff.focusRing}, inset 0 0 0 1.5px ${color.borderStrong}`,
				},
			},
			// the app's signature: pink, ink outline, solid drip shadow (pressed: the shadow shrinks)
			containedPrimary: {
				padding: '6px 18px',
				color: '#fff',
				backgroundColor: color.pink,
				border: action.border,
				boxShadow: action.shadow,
				fontFamily: font.display,
				fontWeight: 400,
				fontSize: '1.05rem',
				letterSpacing: '.6px',
				lineHeight: 1.45,
				transition: 'transform .15s cubic-bezier(.2,.8,.2,1), box-shadow .15s cubic-bezier(.2,.8,.2,1), background-color .2s',
				'&:hover': {
					backgroundColor: color.pinkHover,
					boxShadow: action.shadowHover,
					transform: 'translateY(-1px)',
					'@media (hover: none)': {
						backgroundColor: color.pink,
					},
				},
				'&:active': {
					boxShadow: action.shadowPressed,
					transform: 'translateY(3px)',
				},
				'&$focusVisible': {
					boxShadow: `${action.shadow}, ${graff.focusRing}`,
				},
				'&$disabled': {
					border: `2px solid ${color.borderStrong}`,
					boxShadow: 'none',
				},
			},
			containedSecondary: {
				color: color.ink,
				backgroundColor: color.cyan,
				'&:hover': {
					backgroundColor: '#56DCFA',
					'@media (hover: none)': {
						backgroundColor: color.cyan,
					},
				},
			},
			containedSizeSmall: {
				padding: '5px 12px',
			},
		},

		//---- tabs ----//
		MuiTabs: {
			indicator: {
				height: 3,
				borderRadius: 3,
				backgroundColor: color.cyan,
			},
		},
		PrivateTabIndicator: {
			colorPrimary: { backgroundColor: color.cyan },
			colorSecondary: { backgroundColor: color.cyan },
		},
		MuiTab: {
			root: {
				fontFamily: font.body,
				fontWeight: 600,
				textTransform: 'none',
				minWidth: 'auto',
				padding: '6px 16px',
				'@media (min-width: 600px)': {
					minWidth: 'auto',
				},
			},
			textColorPrimary: {
				color: color.muted,
				'&$selected': {
					color: color.ink,
				},
			},
			textColorSecondary: {
				color: color.muted,
				'&$selected': {
					color: color.ink,
				},
			},
		},

		//---- surfaces ----//
		MuiPaper: {
			rounded: {
				borderRadius: radius.md,
			},
		},
		MuiCard: {
			root: {
				borderRadius: radius.lg,
				boxShadow: shadow.md,
			},
		},
		MuiCardHeader: {
			title: {
				fontFamily: font.display,
			},
		},
		MuiDialog: {
			paper: {
				borderRadius: radius.lg,
				boxShadow: shadow.lg,
			},
		},
		MuiDrawer: {
			paper: {
				backgroundColor: 'rgba(255,255,255,.72)',
				backdropFilter: 'blur(8px)',
				borderRight: `1px solid ${color.border}`,
			},
			paperAnchorDockedLeft: {
				borderRight: `1px solid ${color.border}`,
			},
		},

		//---- fields ----//
		MuiOutlinedInput: {
			root: {
				borderRadius: radius.sm,
				transition: 'box-shadow .2s',
				'&:hover $notchedOutline': {
					borderColor: color.borderStrong,
				},
				'&$focused': {
					boxShadow: graff.focusRing,
				},
				'&$focused $notchedOutline': {
					borderColor: color.cyan,
					borderWidth: 1.5,
				},
			},
			notchedOutline: {
				borderColor: color.borderStrong,
			},
		},
		MuiInput: {
			underline: {
				'&:after': {
					borderBottomColor: color.cyan,
				},
			},
		},
		MuiFilledInput: {
			root: {
				borderTopLeftRadius: radius.sm,
				borderTopRightRadius: radius.sm,
				backgroundColor: color.sunken,
				'&:hover': {
					backgroundColor: '#EEEAE1',
				},
				'&$focused': {
					backgroundColor: color.sunken,
				},
			},
			underline: {
				'&:after': {
					borderBottomColor: color.cyan,
				},
			},
		},
		MuiFormLabel: {
			root: {
				'&$focused': {
					color: color.cyanInk,
				},
			},
		},

		//---- small things ----//
		MuiChip: {
			root: {
				borderRadius: 999,
				fontFamily: font.body,
				fontWeight: 600,
			},
		},
		MuiTooltip: {
			tooltip: {
				backgroundColor: color.ink,
				borderRadius: 8,
				fontFamily: font.body,
				fontSize: '0.75rem',
				fontWeight: 600,
				padding: '6px 10px',
			},
			arrow: {
				color: color.ink,
			},
		},
		// switches read as "on" in cyan, never pink
		MuiSwitch: {
			colorPrimary: {
				'&$checked': {
					color: color.cyan,
				},
				'&$checked + $track': {
					backgroundColor: color.cyan,
				},
			},
			colorSecondary: {
				'&$checked': {
					color: color.cyan,
				},
				'&$checked + $track': {
					backgroundColor: color.cyan,
				},
			},
		},
	},
});

const CustomThemeProvider = (props) => {
	return (
		<StylesProvider jss={jss} generateClassName={generateClassName}>
			<MuiThemeProvider theme={theme}>{props.children}</MuiThemeProvider>
		</StylesProvider>
	);
};
export default CustomThemeProvider;
